import { Fragment, memo, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { IconButton, WithTooltip } from 'basic-components';
import { ArrowLeft, Copy, Exit, History, Logs } from 'basic-components/icons';
import { StatusesEnums } from 'dashboard-services/api/statuses';
import { CommonService } from 'dashboard-services/services';
import TimeService from 'dashboard-services/services/TimeService';
import { DashboardLinkFactory } from 'ng-common-features/urlBuilder';
import { globalActions } from 'primary-components';

import { statusesActions } from 'actions/modules/statuses';
import { statusesQueries } from 'queries/modules/statuses';
import { When } from 'react-if';

import './Header.scss';

const EMPTY_OBJ = {};

const Header = ({ startTime = "", name = "", version = undefined, type = undefined, id = undefined, fileName = "", payload = EMPTY_OBJ, formula = undefined }) => {
  const dispatch = useDispatch(),
        definedTime = useSelector(state => state.myAccountState.settingsState.definedTime),
        jobId = useSelector(state => state.statusesState.jobId),
        goToHistory = useCallback(() => dispatch(statusesActions.showHistory({ formula, name, type })), [dispatch, formula, name, type]),
        goToLogs = useCallback(() => {
          const url = DashboardLinkFactory.logs.job({ jobId })
          CommonService.openInNewTab(url)
        }, [jobId]),
        copyJobId = useCallback(() => {
          navigator.clipboard.writeText(jobId)
            .then(() => dispatch(globalActions.getMessageStore()).success("JobId copied to clipboard."))
        }, [dispatch, jobId]),
        formattedStartTime = useMemo(() => startTime ? TimeService.getUserFormattedDateTime(startTime, definedTime) : "", [startTime, definedTime]),
        back = useCallback(() => dispatch(statusesActions.back()), [dispatch]),
        openItemRaw = statusesQueries.useOpenBasedOnStatusType({ formula, jobId, param: type === StatusesEnums.STATUS_TYPE.ETL ? (payload?.groupName || name) : id, type }),
        openItem = useCallback(e => {
          e.preventDefault()
          e.stopPropagation()
          openItemRaw()
        }, [openItemRaw])
  return (
    <div className="statuses__details__header">
      <div 
          className="statuses__details__header__back"
          onClick={back}
      >
        <ArrowLeft
            color="grey-medium"
            height={24}
            width={24}
        />
      </div>
      <div className="statuses__details__header__date">
        {formattedStartTime.split(" ")[0]}
      </div>
      <div className="statuses__details__header__time">
        {formattedStartTime.split(" ")[1]}
      </div>
      <div className="statuses__details__header__name">
        {fileName + (version ? " v."+version : "")}
      </div>
      <div className="statuses__details__header__icons">
        <When condition={name}>
          {() => (
            <Fragment>
              <WithTooltip
                  data-place="top"
                  short
                  text="Last runs"
              >
                <IconButton
                    className="statuses__details__header__icons__history"
                    onClick={goToHistory}
                >
                  <History
                      color="blue-bright"
                      height={16}
                      width={16}
                  />
                </IconButton>
              </WithTooltip>
              <WithTooltip
                  data-place="top"
                  short
                  text="Copy jobId"
              >
                <IconButton
                    className="statuses__details__header__icons__copy"
                    onClick={copyJobId}
                >
                  <Copy
                      color="blue-bright"
                      height={16}
                      width={16}
                  />
                </IconButton>
              </WithTooltip>
              <WithTooltip
                  data-place="top"
                  short
                  text="Open logs"
              >
                <IconButton
                    className="statuses__details__header__icons__logs"
                    onClick={goToLogs}
                >
                  <Logs
                      color="blue-bright"
                      height={16}
                      width={16}
                  />
                </IconButton>
              </WithTooltip>
              <WithTooltip
                  data-place="top"
                  short
                  text="Open"
              >
                <IconButton
                    className="statuses__details__header__icons__exit"
                    onClick={openItem}
                >
                  <Exit
                      color="blue-bright"
                      height={16}
                      width={16}
                  />
                </IconButton>
              </WithTooltip>
            </Fragment>
          )}
        </When>
      </div>
    </div>
  )
}

Header.propTypes = {
  fileName: PropTypes.string,
  formula: PropTypes.object,
  id: PropTypes.string,
  name: PropTypes.string,
  payload: PropTypes.object,
  startTime: PropTypes.string,
  type: PropTypes.string,
  version: PropTypes.string,
}

export default memo(Header);
